import { FC, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';

import logo from '@/assets/img/login-bg.png';
import bgSvg from '@/assets/svg/bg-clip-path.svg';
import MainLogo from '@/assets/svg/main-logo.svg?react';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { TwoFactorAuth } from '@/components/TwoFactorAuth/TwoFactorAuth';

import { useAuthStore } from '@/stores/authStore';

interface LoginParams {
  username: string;
  password: string;
}

export const LoginPage: FC = () => {
  const { signIn, verifyTwoFactor } = useAuthStore();
  const isMobile = useIsMobile();
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState<{
    totpKey: string;
    totpSecret: string;
    is2FAEnabled: boolean;
  } | null>(null);

  const onSubmit = async (data: LoginParams) => {
    try {
      const response = await signIn(
        data.username.toLowerCase().trim(),
        data.password,
      );
      setTwoFactorData({
        totpKey: response.totpKey || '',
        totpSecret: response.totpSecret || '',
        is2FAEnabled: response.is2FAEnabled,
      });
    } catch (error) {
      form.setError('password', { message: 'Неправильний логін або пароль' });
    }
  };

  const handleVerify2FA = async (otp: string) => {
    const { username, password } = form.getValues();
    try {
      return await verifyTwoFactor({
        otp,
        username: username.toLowerCase().trim(),
        password,
      });
    } catch (error) {
      throw error;
    }
  };

  const form = useForm<LoginParams>();

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="relative flex h-screen w-full flex-col rounded-r-[20px] md:flex-row">
        {!isMobile && (
          <div className="relative z-10 w-1/2 overflow-hidden rounded-r-[20px]">
            <MainLogo className="absolute right-10 top-10 z-20 h-[56px] w-[200px] fill-[#F58426]" />
            <div className="relative z-10 h-full w-full rounded-r-[20px]">
              <div className="relative h-full w-full bg-[rgba(0,44,65,0.6)]">
                <img
                  src={logo}
                  alt="Фотоархів"
                  className="relative -z-10 h-full w-full object-cover shadow-[0px_0px_20px_10px_rgba(0,0,0,0.25)]"
                />
              </div>
            </div>
          </div>
        )}

        <div className="relative z-10 flex w-full flex-1 items-center justify-center bg-blue-800 p-8 sm:w-1/2 sm:flex-none sm:bg-white">
          {isMobile && (
            <img
              src={bgSvg}
              alt="Фотоархів"
              className="absolute bottom-0 left-0 right-0 top-0 z-0 h-full w-full rounded-r-[20px] object-cover shadow-[0px_0px_20px_10px_rgba(0,0,0,0.25)]"
            />
          )}
          <div className="z-10 flex h-[450px] w-[400px] flex-col rounded-[20px] bg-white px-5 pb-[48px] pt-[38px] shadow-sm">
            <h2 className="mb-2 text-center text-[32px] font-semibold leading-[40px] text-primary">
              Автентифікація
            </h2>
            <p className="text-center text-base text-blue-500">
              Увійти у Ваш аккаунт Фотоархів
            </p>

            {twoFactorData ? (
              <TwoFactorAuth
                totpKey={twoFactorData.totpKey}
                totpSecret={twoFactorData.totpSecret}
                onVerify={handleVerify2FA}
                isFirstTime={!twoFactorData.is2FAEnabled}
              />
            ) : (
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="mt-[54px] flex w-full flex-col items-center"
                >
                  <FormField
                    control={form.control}
                    name="username"
                    rules={{ required: "Обов'язкове поле" }}
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControl>
                          <Input
                            placeholder="Логін"
                            {...field}
                            value={field.value || ''}
                            className="mb-[30px] w-full"
                            autoComplete="new-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    rules={{ required: "Обов'язкове поле" }}
                    render={({ field }) => (
                      <FormItem className="relative mb-[54px] w-full">
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              value={field.value || ''}
                              className="w-full bg-transparent"
                              placeholder="Пароль"
                              type={showPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                              required
                            />
                            <button
                              className="absolute right-[12px] top-1/2 z-50 -translate-y-1/2 cursor-pointer"
                              onClick={() => setShowPassword(!showPassword)}
                              type="button"
                            >
                              {showPassword ? (
                                <EyeOff className="w-[16px] text-primary" />
                              ) : (
                                <Eye className="w-[16px] text-primary" />
                              )}
                            </button>
                            <div className="absolute left-0 top-full w-full">
                              <FormMessage />
                            </div>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? 'заходимо...' : 'Увійти'}
                  </Button>
                  <FormMessage />
                </form>
              </Form>
            )}
          </div>
          <span className="absolute bottom-[14px] left-1/2 w-full -translate-x-1/2 text-center text-base text-gray-200">
            © 1921-{new Date().getFullYear()} ПАТ «НАСК «ОРАНТА».
          </span>
        </div>
      </div>
    </div>
  );
};
